/* PlayerChampions — iter516 organizer dashboard tile showing 4 champion
   categories with Week / Month / All-Time tabs. Data comes from
   GET /api/sports/players/champions?window=week|month|all.

   Categories (top 3 each):
     👑 Top Rated       — highest true_rating
     📈 Most Improved   — biggest rating gain in window
     ⚔️ Iron Player     — most games played in window
     🏆 Champion Streak — highest win rate in window
*/
import { useEffect, useState, useCallback } from 'react';
import { Crown, TrendingUp, Swords, Trophy, Loader2 } from 'lucide-react';
import { sportsApi } from '../SportsAuthContext';

const WINDOWS = [
  { key: 'week',  label: 'Week'  },
  { key: 'month', label: 'Month' },
  { key: 'all',   label: 'All Time' },
];

const CATEGORY_META = {
  top_rated:       { title: 'Top Rated',       Icon: Crown,      accent: '#F59E0B',
                     subtitle: 'Highest skill rating',
                     valueOf: r => r.true_rating.toFixed(2),
                     valueLabel: r => `${r.wins}W · ${r.losses}L · ${r.games_played} games` },
  most_improved:   { title: 'Most Improved',   Icon: TrendingUp, accent: '#22C55E',
                     subtitle: 'Biggest rating climb',
                     valueOf: r => (r.gain >= 0 ? '+' : '') + r.gain.toFixed(2),
                     valueLabel: r => `${r.starting_rating} → ${r.current_rating} · ${r.games_in_window} games` },
  iron_player:     { title: 'Iron Player',     Icon: Swords,     accent: '#3B82F6',
                     subtitle: 'Most games played',
                     valueOf: r => r.games_in_window,
                     valueLabel: r => `games · rating ${r.true_rating}` },
  champion_streak: { title: 'Champion Streak', Icon: Trophy,     accent: '#E63946',
                     subtitle: 'Highest win rate',
                     valueOf: r => Math.round(r.win_rate * 100) + '%',
                     valueLabel: r => `${r.wins_in_window}W of ${r.games_in_window}` },
};

function RankBadge({ rank }) {
  const styles = {
    1: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B' },
    2: { bg: 'rgba(163,163,163,0.15)', color: '#A3A3A3' },
    3: { bg: 'rgba(180,83,9,0.15)',   color: '#B45309' },
  };
  const s = styles[rank] || styles[3];
  return (
    <div style={{
      width: 22, height: 22, borderRadius: '50%',
      background: s.bg, color: s.color, flexShrink: 0,
      fontSize: 11, fontWeight: 900, fontFamily: 'Oswald, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{rank}</div>
  );
}

function CategoryCard({ meta, rows }) {
  const { title, Icon, accent, subtitle, valueOf, valueLabel } = meta;
  return (
    <div className="rounded-xl p-4" data-testid={`champ-card-${title.toLowerCase().replace(/ /g, '-')}`}
      style={{ background: '#141414', border: `1px solid ${accent}22` }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <p className="text-xs font-black uppercase tracking-wider"
          style={{ color: accent, fontFamily: 'Oswald, sans-serif' }}>{title}</p>
      </div>
      <p className="text-[10px] mb-3" style={{ color: '#555' }}>{subtitle}</p>
      {rows.length === 0 ? (
        <p className="text-xs italic py-2" style={{ color: '#444' }}>
          No qualifiers yet — play a few more matches.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.profile_id} className="flex items-center gap-2.5">
              <RankBadge rank={i + 1} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: '#fff' }}>{r.name}</p>
                <p className="text-[10px]" style={{ color: '#666' }}>{valueLabel(r)}</p>
              </div>
              <p className="text-sm font-black" style={{ color: accent, fontFamily: 'Oswald, sans-serif' }}>
                {valueOf(r)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PlayerChampions() {
  const [win, setWin] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const r = await sportsApi.get(`/sports/players/champions`, { params: { window: w } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  }, []);

  useEffect(() => { load(win); }, [win, load]);

  return (
    <div className="rounded-xl p-5" data-testid="player-champions"
      style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Trophy className="h-3.5 w-3.5" style={{ color: '#F59E0B' }} />
        <h3 className="text-xs uppercase tracking-wider font-bold"
          style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>Player Champions</h3>
        <span className="text-[10px]" style={{ color: '#444' }}>
          · Competitive-Mode players only · min 5 games
        </span>
        <div className="ml-auto flex gap-1 rounded-lg p-0.5"
          style={{ background: '#0A0A0A', border: '1px solid #262626' }}>
          {WINDOWS.map(w => (
            <button key={w.key} data-testid={`champ-window-${w.key}`}
              onClick={() => setWin(w.key)}
              className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all"
              style={{
                background: win === w.key ? '#E63946' : 'transparent',
                color: win === w.key ? '#fff' : '#737373',
              }}>{w.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#525252' }} />
        </div>
      ) : !data || data.eligible_profile_count === 0 ? (
        <p className="text-xs italic text-center py-6" style={{ color: '#525252' }}>
          No Competitive-Mode players with 5+ games yet — champions will appear as your sessions accumulate history.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CategoryCard meta={CATEGORY_META.top_rated}       rows={data.categories.top_rated || []} />
          <CategoryCard meta={CATEGORY_META.most_improved}   rows={data.categories.most_improved || []} />
          <CategoryCard meta={CATEGORY_META.iron_player}     rows={data.categories.iron_player || []} />
          <CategoryCard meta={CATEGORY_META.champion_streak} rows={data.categories.champion_streak || []} />
        </div>
      )}
    </div>
  );
}
